import {Box, IconButton} from '@mui/material';
import React, { ReactNode, useEffect } from 'react';
import styles from './base-form.module.scss';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormikContext } from 'formik';
import useSnack from '../../../../hooks/useSnack';
import Breadcrumbs from '../../../breadcrumbs';
import { useMediaQuery } from 'react-responsive';
import { Lx } from '../../../../i18n/consts';
import classNames from 'classnames';
import { SVGS } from '../../../../assets/images';

interface Props {
    contentGap?: number;
    formType: 'create' | 'edit';
    children: ReactNode;
    elementName?: string;
    savingTitle: string;
    onClose: () => void;
    onAfterSave?: () => void;
    formHeader: string;
    formHeaderDefaultMessage?: string;
    allowPristineSubmit?: boolean;
    primaryButtonLabel?: string;
    secondaryButtonLabel?: string;
    disableDirtyCheck?: boolean;
    disablePrimaryAction?: boolean;
    onPrimaryAction?: () => Promise<void> | void;
    showSuccessSnack?: boolean;
    bodyClassName?: string;
    footerClassName?: string;
}

export default function BaseForm(props: Props) {
    const { submitForm, isValid, validateForm, dirty } = useFormikContext();
    const contentGap = props.contentGap ?? 0;
    const formBodyStyle = React.useMemo(() => ({
        '--base-form-gap': `${contentGap}px`,
    }) as React.CSSProperties, [contentGap]);
    const snack = useSnack();
    const isMobile = useMediaQuery({ maxWidth: 1000  });
    const intl = useIntl();

    const primaryActionLabel = props.primaryButtonLabel ?? (
        props.formType === 'create'
            ? intl.formatMessage({id: Lx.General.CREATE})
            : intl.formatMessage({id: Lx.General.SAVE})
    );
    const secondaryActionLabel = props.secondaryButtonLabel ?? intl.formatMessage({id: Lx.General.CANCEL});
    const isDirtyCheckDisabled = props.disableDirtyCheck ?? false;
    const showSuccessSnack = props.showSuccessSnack ?? true;
    const isPrimaryActionDisabled = props.disablePrimaryAction ?? (
        (!props.allowPristineSubmit && !dirty && !isDirtyCheckDisabled) || !isValid
    );

    const onSave = async () => {
        if (props.onPrimaryAction) {
            try {
                await props.onPrimaryAction();
            }
            catch(error) {
                console.log(error);
                snack('An unexpected error occurred', 'error');
            }

            return;
        }

        let submitted = false;
        try {
            const hasErrors = Object.keys(await validateForm()).length !== 0;
            if (!hasErrors && (dirty || props.allowPristineSubmit || isDirtyCheckDisabled)) {
                await submitForm();
                if (showSuccessSnack) {
                    snack(`Updated: ${props.savingTitle}`, 'success');
                }
                submitted = true;
            }
        }
        catch(error) {
            console.log(error);
            snack('An unexpected error occurred', 'error');
        }

        if (submitted && props.onAfterSave) {
            props.onAfterSave();
        }
    };

    useEffect(() => {
        validateForm();
    }, []);

    return (
        <div className={styles.formRoot}>
            <Box className={styles.formHeader}>
                <div className={styles.formHeaderContainer}>
                    <div className={styles.conteiner}>
                        <div className={styles.containerItem}>
                            {
                                isMobile
                                    ? <div className={styles.mobileHeaderContainer}>
                                        <IconButton aria-label='leftIconButton' style={{width: '24px', height: '24px', padding: 0}} onClick={props.onClose}>
                                            <SVGS.KeyboardArrowLeftIcon style={{width: '24px', height: '24px', padding: 0}} fill='var(--icons-tertiary)' />
                                        </IconButton>
                                        <div className={styles.headerText}>
                                            <FormattedMessage id={props.formHeader} defaultMessage={props.formHeaderDefaultMessage} />
                                        </div>
                                    </div>
                                    : <><Breadcrumbs />
                                        <div className={styles.headerContainer}>
                                            <div className={styles.headerText}>
                                                <span className={styles.headerTitle}>
                                                    <FormattedMessage id={props.formHeader} defaultMessage={props.formHeaderDefaultMessage} />
                                                </span>
                                                {props.elementName
                                                    ? <div className={styles.selector}>
                                                        :<span className={styles.separator}></span><span className={styles.elementName} title={props.elementName}>{props.elementName}</span>
                                                    </div>
                                                    :<></>}
                                            </div>
                                        </div>
                                    </>
                            }
                        </div>
                        {
                            isMobile
                                ? <></>
                                : <div className={styles.containerItem}>
                                    <button
                                        className={classNames({[styles.button]: true, [styles.cancel]: true})}
                                        type='button'
                                        onClick={props.onClose}>
                                        {secondaryActionLabel}
                                    </button>
                                    <button
                                        className={styles.button}
                                        type='button'
                                        disabled={isPrimaryActionDisabled}
                                        onClick={onSave}>
                                        {primaryActionLabel}
                                    </button>
                                </div>
                        }
                    </div>
                </div>
            </Box>
            <Box className={classNames(styles.formBody, props.bodyClassName)} style={formBodyStyle}>
                {props.children}
            </Box>
            {isMobile && (
                <Box className={classNames(styles.mobileActions, props.footerClassName)}>
                    <button
                        className={classNames(styles.button, styles.mobileActionButton)}
                        type='button'
                        disabled={isPrimaryActionDisabled}
                        onClick={onSave}>{primaryActionLabel}
                    </button>
                    <button
                        className={classNames(styles.button, styles.cancel, styles.mobileActionButton)}
                        type='button'
                        onClick={props.onClose}>{secondaryActionLabel}
                    </button>
                </Box>
            )}
        </div>
    );
}
